// Ta imot service (/bookingbike): finn bookingen til kunden som står ved disken. I samme stil som
// startsiden (planer/ny-kundesamhandling-plan.md): stort søkefelt øverst, valg som knapper, og
// bookingene som store kort. Bookinger som skal leveres er gruppert etter leveringsdag, så de
// som skal inn i dag står først.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { FARGER, FORSIDE } from "./ny-kundesamhandling/handlinger";
import Ikon from "./ny-kundesamhandling/Ikon";
import { normalizeWorkshopSlug, prettifyStoreName } from "./components/workshop/workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUSER = [
  { id: "booking", tittel: "Skal leveres" },
  { id: "mottatt", tittel: "Mottatt" },
  { id: "avsluttet", tittel: "Avsluttet" },
  { id: "kansellert", tittel: "Kansellert" },
  { id: "Alle", tittel: "Alle" },
];

const BOOKINGTYPER = {
  Dag: "Dag",
  Tidliguke: "Tidlig i uka",
  Senuke: "Sent i uka",
  Uke: "Uke",
  Fortlopende: "Fortløpende",
  Overstyrt: "Overstyrt",
  akuttbooking: "Akutt",
  chatbooking: "Chat",
};

function formatNOK(n) {
  return new Intl.NumberFormat("no-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(Number(n) || 0);
}

function formatMinutes(min) {
  const m = Math.max(0, Number(min) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} t ${r} min`;
  if (h) return `${h} t`;
  return `${r} min`;
}

function parseDateSafe(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt) ? dt : null;
}

const startPaDagen = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const dagerTil = (d) => Math.round((startPaDagen(d) - startPaDagen(new Date())) / 86400000);

// «i dag», «i morgen», «i går» eller «tor. 29.10.»
function narTekst(d) {
  if (!d) return "";
  const n = dagerTil(d);
  if (n === 0) return "i dag";
  if (n === 1) return "i morgen";
  if (n === -1) return "i går";
  return d.toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "numeric" });
}

function fbCount(tjeneste) {
  const fb = Array.isArray(tjeneste?.foranBak) ? tjeneste.foranBak.map((v) => String(v).toLowerCase()) : [];
  const c = (fb.includes("foran") ? 1 : 0) + (fb.includes("bak") ? 1 : 0);
  return c || 1;
}

// Bare sifre, uten landkode, så «+47 400 00 000», «0047…» og «40000000» blir like.
function telefonSifre(s) {
  const d = String(s || "").replace(/\D/g, "");
  if (d.length > 8 && d.startsWith("0047")) return d.slice(4);
  if (d.length > 8 && d.startsWith("47")) return d.slice(2);
  return d;
}

function normaliser(b) {
  const items = Array.isArray(b.items) ? b.items : [];
  let totalPrice = 0;
  let totalMinutes = 0;
  let latestFinish = null;
  let earliestDelivery = null;
  let storeRaw = "";
  const produkter = [];

  for (const prod of items) {
    if (!storeRaw) storeRaw = prod?.verkstedSlug || prod?.butikkSlug || prod?.butikk || "";
    const levering = parseDateSafe(prod?.leveringsDato);
    if (levering && (!earliestDelivery || levering < earliestDelivery)) earliestDelivery = levering;
    const ferdig = parseDateSafe(prod?.forventetFerdigDato);
    if (ferdig && (!latestFinish || ferdig > latestFinish)) latestFinish = ferdig;

    const tlist = Array.isArray(prod?.items) ? prod.items : Array.isArray(prod?.tjenester) ? prod.tjenester : [];
    for (const t of tlist) {
      if (typeof t?.pris === "number") totalPrice += (Number(t.pris) || 0) * fbCount(t);
      if (typeof t?.tidsbruk === "number") totalMinutes += Math.max(0, Number(t.tidsbruk) || 0);
    }
    produkter.push({
      // Ski-flyten (bestillingstype «ski») tar både ski og skøyter; produkttypen skiller dem.
      ikon: b.bestillingstype === "ski" ? (/sk[øo]yt/i.test(prod?.produkttype || "") ? "skoyte" : "snofnugg") : "sykkel",
      navn: [prod?.produktmerke || prod?.produktnavn || "Produkt", prod?.farge ? `(${prod.farge})` : ""].filter(Boolean).join(" "),
      type: prod?.produkttype || "",
      tjenester: tlist.map((t) => t?.produktnavn || t?.navn || "").filter(Boolean),
    });
  }

  const c = b.customer || {};
  const customerName = `${c.firstName || ""} ${c.lastName || ""}`.trim();
  const messages = Array.isArray(b.messages) ? b.messages : [];
  const unreadCustomerCount = messages.filter((m) => m.from === "customer" && !m.readAt).length;
  const slug = normalizeWorkshopSlug(storeRaw);

  return {
    id: b._id,
    status: b.status || "booking",
    createdAt: parseDateSafe(b.createdAt),
    customerName,
    phone: c.phoneNumber || "",
    phoneSifre: telefonSifre(c.phoneNumber),
    email: c.email || "",
    butikkSlug: slug,
    butikkNavn: slug ? prettifyStoreName(slug) : "Uten verksted",
    bookingtype: items[0]?.bookingtype || "",
    produkter,
    totalPrice,
    totalMinutes,
    levering: earliestDelivery,
    ferdig: latestFinish,
    unreadCustomerCount,
    sokeTekst: [customerName, c.email, slug ? prettifyStoreName(slug) : "", ...produkter.map((p) => `${p.navn} ${p.type}`)]
      .join(" ")
      .toLowerCase(),
  };
}

// Alle ordene i søket må finnes i navn, e-post, verksted eller produkt (merke, farge, type); tall søkes i telefonnummeret.
function passer(b, sok) {
  const q = sok.trim().toLowerCase();
  if (!q) return true;
  const sifre = telefonSifre(q);
  if (/^[\d\s+]+$/.test(q) && sifre.length >= 3) return b.phoneSifre.includes(sifre);
  return q.split(/\s+/).every((ord) => b.sokeTekst.includes(ord) || (/^\d{3,}$/.test(ord) && b.phoneSifre.includes(ord)));
}

// Gruppene for «Skal leveres». Ellers er det én liste, nyeste først.
function grupper(liste, status) {
  if (status !== "booking") {
    return [{ id: "alle", tittel: null, liste: [...liste].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) }];
  }
  const etterLevering = [...liste].sort((a, b) => (a.levering || Infinity) - (b.levering || Infinity));
  const g = [
    { id: "forsinket", tittel: "Skulle vært levert", tone: "amber", liste: [] },
    { id: "idag", tittel: "Leveres i dag", tone: "emerald", liste: [] },
    { id: "imorgen", tittel: "Leveres i morgen", liste: [] },
    { id: "senere", tittel: "Senere", liste: [] },
    { id: "utenDato", tittel: "Uten leveringsdato", liste: [] },
  ];
  const finn = (id) => g.find((x) => x.id === id);
  for (const b of etterLevering) {
    if (!b.levering) finn("utenDato").liste.push(b);
    else {
      const n = dagerTil(b.levering);
      finn(n < 0 ? "forsinket" : n === 0 ? "idag" : n === 1 ? "imorgen" : "senere").liste.push(b);
    }
  }
  return g.filter((x) => x.liste.length);
}

function Velger({ valgt, onVelg, children }) {
  return (
    <button
      type="button"
      onClick={onVelg}
      aria-pressed={valgt}
      className={`min-h-[2.25rem] rounded-xl border-2 px-3 py-1.5 text-base font-semibold transition active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
        valgt ? "border-indigo-700 bg-indigo-700 text-white" : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function Melding({ children }) {
  return <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">{children}</p>;
}

function BookingKort({ b, visStatus }) {
  const forsinket = b.status === "booking" && b.levering && dagerTil(b.levering) < 0;
  return (
    <Link
      to={`/booking/${b.id}`}
      className="group flex h-full flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-white p-3 transition hover:border-emerald-300 hover:bg-emerald-50/40 active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
    >
      <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
        <div className="min-w-0 flex-1">
          <span className="block text-base font-bold leading-tight text-slate-900 [overflow-wrap:anywhere]">{b.customerName || "Uten navn"}</span>
          <span className="block font-mono text-sm text-slate-600">{b.phone || "Uten telefon"}</span>
        </div>
        {b.unreadCustomerCount > 0 && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-sm font-bold text-white">
            <Ikon navn="melding" className="w-4 h-4" />
            {b.unreadCustomerCount}
            <span className="sr-only"> uleste meldinger</span>
          </span>
        )}
      </div>

      {b.produkter.length > 0 && (
        <ul className="space-y-1">
          {b.produkter.map((p, i) => (
            <li key={i} className="flex min-w-0 items-start gap-2 text-slate-800">
              <Ikon navn={p.ikon} className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="font-semibold">{p.navn}</span>
                {p.type && <span className="text-slate-600"> – {p.type}</span>}
                {p.tjenester.length > 0 && <span className="block text-sm text-slate-600">{p.tjenester.join(", ")}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      <dl className="mt-auto grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-200 pt-3 text-sm">
        <div className="min-w-0">
          <dt className="text-slate-600">Leveres</dt>
          <dd className={`font-semibold first-letter:uppercase ${forsinket ? "text-amber-800" : "text-slate-900"}`}>{narTekst(b.levering) || "Ikke satt"}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-slate-600">Ferdig</dt>
          <dd className="font-semibold text-slate-900 first-letter:uppercase">{narTekst(b.ferdig) || "Ikke satt"}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-slate-600">Verksted</dt>
          <dd className="font-semibold text-slate-900">{b.butikkNavn}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-slate-600">Pris og tid</dt>
          <dd className="font-semibold text-slate-900">
            {formatNOK(b.totalPrice)} · {formatMinutes(b.totalMinutes)}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        {b.bookingtype && (
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700">
            {BOOKINGTYPER[b.bookingtype] || b.bookingtype}
          </span>
        )}
        {visStatus && (
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700 first-letter:uppercase">{b.status}</span>
        )}
        <span className="ml-auto flex items-center gap-1 font-semibold text-emerald-800">
          {b.status === "booking" ? "Ta imot" : "Åpne"}
          <Ikon navn="pil" className="w-5 h-5 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function BookingBike() {
  const [bookings, setBookings] = useState([]);
  const [sok, setSok] = useState("");
  // Alltid «Skal leveres» når siden åpnes, og andre valg huskes ikke (vinnsel, 27.10): en lagret
  // status gjorde at ansatte ikke fant bookingen kunden kom med.
  const [status, setStatus] = useState("booking");
  const [butikk, setButikk] = useState("");
  const [loading, setLoading] = useState(true);
  const [feil, setFeil] = useState("");

  // Rydder bort statusen som ble lagret før.
  useEffect(() => {
    Cookies.remove("bookingStatusFilter");
  }, []);

  useEffect(() => {
    let avbrutt = false;
    (async () => {
      setLoading(true);
      setFeil("");
      try {
        const url = status !== "Alle" ? `${API_BASE_URL}/bookings?status=${encodeURIComponent(status)}` : `${API_BASE_URL}/bookings`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const raw = await r.json();
        if (!avbrutt) setBookings((raw || []).map(normaliser));
      } catch (e) {
        console.error("Feil ved henting av bookinger:", e);
        if (!avbrutt) {
          setBookings([]);
          setFeil("Klarte ikke å hente bookingene. Last siden på nytt om litt.");
        }
      } finally {
        if (!avbrutt) setLoading(false);
      }
    })();
    return () => { avbrutt = true; };
  }, [status]);

  const velgStatus = (id) => {
    setStatus(id);
  };

  // Verkstedene som finnes i lista; velgeren vises bare når det er flere enn ett.
  const verksteder = useMemo(() => {
    const m = new Map();
    for (const b of bookings) if (!m.has(b.butikkSlug)) m.set(b.butikkSlug, b.butikkNavn);
    return [...m.entries()].map(([slug, navn]) => ({ slug, navn })).sort((a, b) => a.navn.localeCompare(b.navn, "nb"));
  }, [bookings]);
  const butikkFinnes = !butikk || verksteder.some((v) => v.slug === butikk);

  const filtrert = useMemo(
    () => bookings.filter((b) => (!butikk || !butikkFinnes || b.butikkSlug === butikk) && passer(b, sok)),
    [bookings, butikk, butikkFinnes, sok]
  );
  const gruppert = grupper(filtrert, status);

  const uleste = filtrert.filter((b) => b.unreadCustomerCount > 0);
  const antallUleste = uleste.reduce((s, b) => s + b.unreadCustomerCount, 0);
  const harSok = sok.trim().length > 0;
  const statusTittel = STATUSER.find((s) => s.id === status)?.tittel.toLowerCase();

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Link to={FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake til kontrollpanelet
      </Link>

      <header className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${FARGER.verksted.ikon}`}>
          <Ikon navn="sykkel" className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance">Ta imot service</h1>
          <p className="text-slate-600">Booket sykkel, ski og skøyter. Finn bookingen til kunden som leverer.</p>
        </div>
      </header>

      <section aria-label="Søk og utvalg" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="tis-sok" className="mb-1 block text-base font-semibold text-slate-800">
            Finn kunde
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <Ikon navn="sok" className="w-6 h-6" />
            </span>
            <input
              id="tis-sok"
              type="search"
              inputMode="search"
              autoComplete="off"
              autoFocus
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              placeholder="Navn, telefon eller merke"
              aria-describedby="tis-sok-status"
              className="w-full rounded-xl border-2 border-slate-300 bg-white py-3.5 pl-12 pr-4 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
            />
          </div>
          <p id="tis-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {loading ? "" : harSok ? `${filtrert.length} ${filtrert.length === 1 ? "booking" : "bookinger"} passer` : ""}
          </p>
        </div>

        <div role="group" aria-labelledby="tis-status" className="space-y-1.5">
          <span id="tis-status" className="block text-sm font-semibold text-slate-700">Status</span>
          <div className="flex flex-wrap gap-2">
            {STATUSER.map((s) => (
              <Velger key={s.id} valgt={status === s.id} onVelg={() => velgStatus(s.id)}>{s.tittel}</Velger>
            ))}
          </div>
        </div>

        {verksteder.length > 1 && (
          <div role="group" aria-labelledby="tis-verksted" className="space-y-1.5">
            <span id="tis-verksted" className="block text-sm font-semibold text-slate-700">Verksted</span>
            <div className="flex flex-wrap gap-2">
              <Velger valgt={!butikk || !butikkFinnes} onVelg={() => setButikk("")}>Alle verksteder</Velger>
              {verksteder.map((v) => (
                <Velger key={v.slug} valgt={butikk === v.slug} onVelg={() => setButikk(v.slug)}>{v.navn}</Velger>
              ))}
            </div>
          </div>
        )}
      </section>

      {antallUleste > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border-2 border-red-200 bg-red-50 p-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-600 text-white">
            <Ikon navn="melding" className="w-6 h-6" />
          </span>
          <p className="min-w-0 flex-1 text-red-900">
            <span className="block font-semibold">Uleste meldinger fra kunder</span>
            <span className="block text-sm">
              {antallUleste} {antallUleste === 1 ? "melding" : "meldinger"} i {uleste.length} {uleste.length === 1 ? "booking" : "bookinger"}.
            </span>
          </p>
          <Link
            to={`/booking/${uleste[0].id}`}
            className="min-h-[2.25rem] rounded-xl bg-red-700 px-3 py-1.5 font-semibold text-white hover:bg-red-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            Åpne første
          </Link>
        </div>
      )}

      <div aria-live="polite" className="space-y-4">
        {feil ? (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>
        ) : loading ? (
          <Melding>Henter bookinger …</Melding>
        ) : filtrert.length === 0 ? (
          <Melding>
            {harSok
              ? `Ingen bookinger med status «${statusTittel}» passer søket. Prøv et annet navn eller nummer, eller velg «Alle».`
              : `Ingen bookinger med status «${statusTittel}».`}
          </Melding>
        ) : (
          gruppert.map((g) => (
            <section key={g.id} aria-labelledby={g.tittel ? `tis-g-${g.id}` : undefined} aria-label={g.tittel ? undefined : "Bookinger"}>
              {g.tittel && (
                <h2 id={`tis-g-${g.id}`} className="mb-3 flex flex-wrap items-center gap-2 text-base font-bold text-slate-900">
                  {g.tittel}
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${
                      g.tone === "amber" ? "bg-amber-100 text-amber-900" : g.tone === "emerald" ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {g.liste.length}
                  </span>
                </h2>
              )}
              <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {g.liste.map((b) => (
                  <li key={b.id} className="min-w-0">
                    <BookingKort b={b} visStatus={status === "Alle"} />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
